import {
  ArchiveIcon,
  ArrowRightIcon,
  BellIcon,
  BellSimpleIcon,
  CheckIcon,
  ChecksIcon,
  CircleNotchIcon,
} from "@phosphor-icons/react";
import {
  Button,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Separator,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import relativeTime from "dayjs/plugin/relativeTime";
import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { useToast } from "@/client/hooks/use-toast";

import type { NotificationItem } from "./api";
import { CATEGORY_LABELS, getEventDisplay } from "./event-display";
import { usePushNotifications } from "./push";
import {
  markNotificationReadInCaches,
  useArchiveNotification,
  useMarkAllNotificationsAsRead,
  useNotifications,
  useUnreadCount,
} from "./queries";
import { useNotificationBody } from "./use-notification-body";

dayjs.extend(relativeTime);
dayjs.locale("fr");

const groupDayLabel = (date: string | Date) => {
  const day = dayjs(date).startOf("day");

  const today = dayjs().startOf("day");

  const difference = today.diff(day, "day");

  if (difference === 0) {
    return "Aujourd’hui";
  }

  if (difference === 1) {
    return "Hier";
  }

  if (difference < 7) {
    return dayjs(date).format("dddd");
  }

  return dayjs(date).format("D MMMM YYYY");
};

export const groupByDay = (items: NotificationItem[]) => {
  const groups = new Map<string, NotificationItem[]>();

  for (const item of items) {
    const key = groupDayLabel(item.createdAt);

    const group = groups.get(key) ?? [];

    group.push(item);
    groups.set(key, group);
  }

  return [...groups.entries()];
};

type NotificationRowProps = {
  notification: NotificationItem;
  onClick?: (notification: NotificationItem) => void;
  compact?: boolean;
};

export const NotificationRow = ({
  notification,
  onClick,
  compact = false,
}: NotificationRowProps) => {
  const display = getEventDisplay(notification.type);
  const body = useNotificationBody()(notification);

  const Icon = display.icon;

  const archive = useArchiveNotification();

  const categoryLabel = CATEGORY_LABELS[notification.category] ?? display.label;

  return (
    <article
      className={cn(
        "group relative flex w-full items-start gap-3 px-3.5 py-3.5 transition sm:px-4",
        "hover:bg-indigo-50/45",
        !notification.isRead && "bg-indigo-50/35",
      )}
    >
      {!notification.isRead && (
        <span
          aria-label="Notification non lue"
          className="absolute left-1.5 top-5 size-1.5 rounded-full bg-indigo-600"
        />
      )}

      <button
        type="button"
        onClick={() => {
          onClick?.(notification);
        }}
        className="flex min-w-0 flex-1 items-start gap-3 rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        <span
          aria-hidden
          className={cn(
            "grid size-10 shrink-0 place-items-center rounded-xl",
            "ring-1 ring-inset ring-black/[0.03]",
            display.className,
          )}
        >
          <Icon size={19} weight="duotone" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className={cn(
                "line-clamp-1 text-xs leading-5 sm:text-[13px]",
                notification.isRead ? "font-semibold text-slate-700" : "font-bold text-[#071451]",
              )}
            >
              {notification.title ?? display.label}
            </span>

            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[8px] font-bold uppercase tracking-wide text-slate-500">
              {categoryLabel}
            </span>
          </span>

          {!compact && body && (
            <span className="mt-1 line-clamp-2 block text-[10px] leading-4 text-slate-500 sm:text-xs sm:leading-5">
              {body}
            </span>
          )}

          <time
            dateTime={String(notification.createdAt)}
            title={dayjs(notification.createdAt).format("D MMMM YYYY à HH:mm")}
            className="mt-1.5 block text-[9px] font-medium tabular-nums text-slate-400 sm:text-[10px]"
          >
            {dayjs(notification.createdAt).fromNow()}
          </time>
        </span>
      </button>

      <div className="flex shrink-0 items-center gap-0.5 self-center">
        {!notification.isRead && (
          <button
            type="button"
            aria-label="Ouvrir et marquer comme lue"
            title="Marquer comme lue"
            onClick={() => {
              onClick?.(notification);
            }}
            className="grid size-8 place-items-center rounded-lg text-indigo-600 transition hover:bg-white hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <CheckIcon aria-hidden size={15} weight="bold" />
          </button>
        )}

        <button
          type="button"
          aria-label="Archiver la notification"
          title="Archiver"
          disabled={archive.isPending}
          onClick={() => {
            archive.mutate({
              id: notification.id,
            });
          }}
          className="grid size-8 place-items-center rounded-lg text-slate-400 transition hover:bg-white hover:text-indigo-600 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-40"
        >
          {archive.isPending ? (
            <CircleNotchIcon aria-hidden size={15} className="animate-spin" />
          ) : (
            <ArchiveIcon aria-hidden size={15} />
          )}
        </button>
      </div>
    </article>
  );
};

export const NotificationsEmptyState = ({ filtered = false }: { filtered?: boolean }) => (
  <div className="flex min-h-64 flex-col items-center justify-center px-6 py-10 text-center">
    <span className="grid size-16 place-items-center rounded-2xl bg-gradient-to-br from-indigo-50 to-emerald-50 text-indigo-500">
      <BellSimpleIcon aria-hidden size={29} weight="duotone" />
    </span>

    <h2 className="mt-4 text-sm font-bold text-[#071451]">
      {filtered ? "Aucune notification dans cette catégorie" : "Vous êtes à jour"}
    </h2>

    <p className="mt-1.5 max-w-xs text-xs leading-5 text-slate-500">
      {filtered
        ? "Essayez une autre catégorie pour retrouver vos notifications."
        : "Les nouvelles informations concernant votre compte et vos CV apparaîtront ici."}
    </p>
  </div>
);

export const NotificationsSkeleton = ({ compact = false }: { compact?: boolean }) => (
  <div aria-hidden className={compact ? "divide-y divide-slate-100" : "space-y-6"}>
    {Array.from({
      length: compact ? 4 : 2,
    }).map((_, groupIndex) =>
      compact ? (
        <div key={groupIndex} className="flex items-start gap-3 px-4 py-4">
          <div className="size-10 shrink-0 animate-pulse rounded-xl bg-slate-100" />

          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3.5 w-2/5 animate-pulse rounded bg-slate-100" />
            <div className="h-3 w-4/5 animate-pulse rounded bg-slate-100" />
            <div className="h-2.5 w-16 animate-pulse rounded bg-slate-100" />
          </div>
        </div>
      ) : (
        <div key={groupIndex}>
          <div className="mb-2 h-3 w-24 animate-pulse rounded bg-slate-100" />

          <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {Array.from({
              length: groupIndex === 0 ? 3 : 2,
            }).map((__, rowIndex) => (
              <div key={rowIndex} className="flex items-start gap-3 px-4 py-4">
                <div className="size-10 shrink-0 animate-pulse rounded-xl bg-slate-100" />

                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-3.5 w-2/5 animate-pulse rounded bg-slate-100" />
                  <div className="h-3 w-4/5 animate-pulse rounded bg-slate-100" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    )}
  </div>
);

type NotificationCenterProps = {
  trigger?: ReactNode;
  classNameTrigger?: string;
  align?: "start" | "center" | "end";
  allNotificationsHref?: string;
};

export const NotificationCenter = ({
  trigger,
  classNameTrigger,
  align = "end",
  allNotificationsHref = "/client/user-space/notifications",
}: NotificationCenterProps) => {
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { toast } = useToast();

  const push = usePushNotifications();

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useNotifications();

  const unreadCount = useUnreadCount();

  const markAllAsRead = useMarkAllNotificationsAsRead();

  const notifications = data?.pages.flatMap((page) => page.items) ?? [];

  const groups = groupByDay(notifications);

  const count = unreadCount.data?.count ?? 0;

  const activatePush = async () => {
    const result = await push.subscribe();

    if (result.ok) {
      toast({
        variant: "success",
        title: "Notifications push activées",
        description: "Vous recevrez les alertes importantes même lorsque l’application est fermée.",
      });

      return;
    }

    if (result.reason === "denied") {
      toast({
        variant: "destructive",
        title: "Notifications bloquées",
        description:
          "Autorisez les notifications depuis les paramètres de votre navigateur puis réessayez.",
      });

      return;
    }

    toast({
      variant: "destructive",
      title: "Activation impossible",
      description: "Les notifications push n’ont pas pu être activées.",
    });
  };

  const openNotification = (notification: NotificationItem) => {
    markNotificationReadInCaches(queryClient, notification.id);

    setOpen(false);

    void navigate(`/client/user-space/notifications/${notification.id}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Notifications${
            count > 0 ? `, ${count} non lue${count > 1 ? "s" : ""}` : ""
          }`}
          className={cn(
            "product-focus relative grid size-10 place-items-center rounded-xl text-slate-600 transition",
            "hover:bg-indigo-50 hover:text-indigo-700",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
            classNameTrigger,
          )}
        >
          {trigger ?? <BellIcon aria-hidden size={20} weight={count > 0 ? "fill" : "regular"} />}

          {count > 0 && (
            <>
              <span className="absolute right-1 top-1 size-2 animate-ping rounded-full bg-rose-400 opacity-60" />

              <span className="absolute -right-1 -top-1 grid min-h-[18px] min-w-[18px] place-items-center rounded-full border-2 border-white bg-rose-500 px-1 text-[9px] font-bold leading-none text-white shadow-sm">
                {count > 99 ? "99+" : count}
              </span>
            </>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align={align}
        sideOffset={8}
        collisionPadding={8}
        className="w-[calc(100vw-1rem)] overflow-hidden rounded-2xl border border-indigo-100 bg-white p-0 shadow-[0_24px_70px_rgba(15,23,42,0.22)] sm:w-[410px]"
      >
        <PopoverArrow width={16} height={8} className="fill-white" />

        <header className="border-b border-slate-100 bg-gradient-to-r from-indigo-50/70 via-white to-emerald-50/50 px-4 pb-3 pt-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-600">
                Centre d’activité
              </p>

              <div className="mt-1 flex flex-wrap items-center gap-2">
                <h2 className="text-base font-bold text-[#071451]">Notifications</h2>

                {count > 0 && (
                  <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[9px] font-bold text-indigo-700">
                    {count} non lue
                    {count > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>

            {count > 0 && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={markAllAsRead.isPending}
                onClick={() => {
                  markAllAsRead.mutate();
                }}
                className="h-8 shrink-0 gap-1.5 rounded-lg px-2 text-[10px] font-bold text-indigo-700 hover:bg-white"
              >
                {markAllAsRead.isPending ? (
                  <CircleNotchIcon aria-hidden size={14} className="animate-spin" />
                ) : (
                  <ChecksIcon aria-hidden size={14} />
                )}

                <span className="xs:inline hidden">Tout lire</span>
              </Button>
            )}
          </div>
        </header>

        <ScrollArea className="h-[min(460px,62dvh)]">
          {isLoading ? (
            <NotificationsSkeleton compact />
          ) : notifications.length === 0 ? (
            <NotificationsEmptyState />
          ) : (
            <div>
              {groups.map(([day, items]) => (
                <section key={day}>
                  <h3 className="sticky top-0 z-10 border-y border-slate-100 bg-white/95 px-4 py-2 text-[9px] font-bold uppercase tracking-[0.12em] text-slate-400 backdrop-blur">
                    {day}
                  </h3>

                  <div className="divide-y divide-slate-100">
                    {items.map((notification) => (
                      <NotificationRow
                        key={notification.id}
                        notification={notification}
                        onClick={openNotification}
                      />
                    ))}
                  </div>
                </section>
              ))}

              {hasNextPage && (
                <button
                  type="button"
                  disabled={isFetchingNextPage}
                  onClick={() => {
                    void fetchNextPage();
                  }}
                  className="flex min-h-11 w-full items-center justify-center gap-2 border-t border-slate-100 text-[10px] font-bold text-indigo-600 transition hover:bg-indigo-50 disabled:opacity-50"
                >
                  {isFetchingNextPage && (
                    <CircleNotchIcon aria-hidden size={14} className="animate-spin" />
                  )}

                  {isFetchingNextPage ? "Chargement…" : "Charger les plus anciennes"}
                </button>
              )}
            </div>
          )}
        </ScrollArea>

        {push.supported && !push.subscribed && (
          <>
            <Separator />

            <button
              type="button"
              disabled={push.busy}
              onClick={() => {
                void activatePush();
              }}
              className="flex min-h-11 w-full items-center justify-center gap-2 bg-indigo-50/40 px-4 text-[10px] font-bold text-indigo-700 transition hover:bg-indigo-50 disabled:opacity-50"
            >
              <BellIcon aria-hidden size={15} />

              {push.busy ? "Activation…" : "Activer les notifications push"}
            </button>
          </>
        )}

        {allNotificationsHref && (
          <>
            <Separator />

            <Link
              to={allNotificationsHref}
              onClick={() => {
                setOpen(false);
              }}
              className="flex min-h-12 items-center justify-center gap-2 bg-white px-4 text-xs font-bold text-[#071451] transition hover:bg-indigo-50"
            >
              Voir toutes les notifications
              <ArrowRightIcon aria-hidden size={15} weight="bold" />
            </Link>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
